import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Check, ChevronLeft, ChevronRight, Save, AlertTriangle, Info } from 'lucide-react';

interface Djhv {
  candidato_id: number;
  secciones: Record<string, any>;
  secciones_completas: number[];
  estado: 'sin_iniciar' | 'en_proceso' | 'completa' | 'confirmada_declara';
}

const SECCIONES = [
  { n: 1, titulo: 'S1 · Datos personales', desc: 'Domicilio + cargo + circunscripción (auto-RENIEC + manual)' },
  { n: 2, titulo: 'S2 · Experiencia laboral', desc: 'Últimos 10 años · máx 5 registros' },
  { n: 3, titulo: 'S3 · Formación académica', desc: '6 subsecciones (básica, técnica, no univ., univ., posgrado, otros)' },
  { n: 4, titulo: 'S4 · Trayectoria partidaria', desc: 'Cargos partidarios + elección popular · máx 2 cada uno' },
  { n: 5, titulo: 'S5 · Sentencias penales', desc: '⚠ OBLIGATORIO declarar Sí/No · omisión = exclusión automática' },
  { n: 6, titulo: 'S6 · Sentencias no penales', desc: 'Civiles, alimentos, violencia, obligaciones' },
  { n: 7, titulo: 'S7 · Renuncias políticas', desc: 'Máx 2 últimas · OP del ROP' },
  { n: 8, titulo: 'S8 · Ingresos, bienes y rentas', desc: 'Año fiscal 2025 · inmuebles SUNARP · vehículos · acciones' },
  { n: 9, titulo: 'S9 · Información adicional', desc: 'Opcional · aclaraciones a otros rubros' },
];

const ESTADO_PILL: Record<string, string> = {
  sin_iniciar: 'bg-gray-100 text-gray-600',
  en_proceso: 'bg-amber-100 text-amber-700',
  completa: 'bg-blue-100 text-blue-700',
  confirmada_declara: 'bg-green-100 text-green-700',
};

function Field({ label, children, required, hint }: { label: string; children: React.ReactNode; required?: boolean; hint?: string }) {
  return (
    <div>
      <label className="block text-[11px] text-gray-700 mb-1">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function Note({ children, intent = 'info' }: { children: React.ReactNode; intent?: 'info' | 'warn' | 'danger' }) {
  const cls = {
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    warn: 'bg-amber-50 border-amber-200 text-amber-900',
    danger: 'bg-red-50 border-red-200 text-red-900',
  }[intent];
  const Icon = intent === 'danger' ? AlertTriangle : Info;
  return (
    <div className={`border rounded p-2 text-xs flex gap-2 ${cls}`}>
      <Icon size={14} className="shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

function inputCls() {
  return 'w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-arpp-primary';
}

function YesNoToggle({ value, onChange, labelYes = 'Sí tengo información', labelNo = 'No tengo información' }: {
  value: boolean | null | undefined; onChange: (v: boolean) => void; labelYes?: string; labelNo?: string;
}) {
  return (
    <div className="flex gap-2">
      <label className={`flex-1 border rounded p-2 cursor-pointer text-xs flex items-center gap-2 ${value === true ? 'bg-arpp-primary text-white border-arpp-primary' : 'bg-white hover:bg-gray-50'}`}>
        <input type="radio" checked={value === true} onChange={() => onChange(true)} className="m-0" />
        {labelYes}
      </label>
      <label className={`flex-1 border rounded p-2 cursor-pointer text-xs flex items-center gap-2 ${value === false ? 'bg-gray-600 text-white border-gray-600' : 'bg-white hover:bg-gray-50'}`}>
        <input type="radio" checked={value === false} onChange={() => onChange(false)} className="m-0" />
        {labelNo}
      </label>
    </div>
  );
}

// ── S1 · Datos personales ───────────────────────────────────────────────────
function S1({ data, onChange, candidatoExt }: { data: any; onChange: (d: any) => void; candidatoExt: any }) {
  const d = data || {};
  return (
    <div className="space-y-4">
      <Note>
        Los datos en gris son <strong>automáticos en DECLARA+</strong> (RENIEC).
        El personero los confirma y completa los campos manuales abajo.
      </Note>

      <div className="bg-gray-50 border rounded p-3">
        <h5 className="text-[11px] font-semibold uppercase text-gray-600 mb-2">Auto RENIEC (no editable)</h5>
        <div className="grid md:grid-cols-3 gap-2 text-xs">
          <div><span className="text-gray-500">DNI:</span> <strong>{candidatoExt?.dni ?? '—'}</strong></div>
          <div><span className="text-gray-500">Sexo:</span> <strong>{candidatoExt?.sexo ?? '—'}</strong></div>
          <div><span className="text-gray-500">Fnac:</span> <strong>{candidatoExt?.fecha_nacimiento ?? '—'}</strong></div>
          <div className="md:col-span-3"><span className="text-gray-500">Nombres + Apellidos:</span> <strong>{candidatoExt?.apellidos_onpe} {candidatoExt?.nombres_onpe}</strong></div>
          <div className="md:col-span-3"><span className="text-gray-500">Dirección RENIEC:</span> {candidatoExt?.direccion_reniec ?? '—'}</div>
        </div>
      </div>

      <Note intent="warn">
        <strong>Acreditar 2 años de domicilio continuo</strong> en la circunscripción de postulación
        (Art. 245 TUO CPC). Si tu domicilio en DECLARA+ no coincide con la circunscripción,
        debes adjuntar documentos con fecha cierta.
      </Note>

      <div>
        <h5 className="text-xs font-semibold uppercase text-gray-700 mb-2">Domicilio (ingreso manual en DECLARA+)</h5>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Departamento" required><input className={inputCls()} value={d.dpto_dom || ''} onChange={(e) => onChange({ ...d, dpto_dom: e.target.value })} /></Field>
          <Field label="Provincia" required><input className={inputCls()} value={d.prov_dom || ''} onChange={(e) => onChange({ ...d, prov_dom: e.target.value })} /></Field>
          <Field label="Distrito" required><input className={inputCls()} value={d.dist_dom || ''} onChange={(e) => onChange({ ...d, dist_dom: e.target.value })} /></Field>
          <div className="md:col-span-3">
            <Field label="Dirección completa (Av./Jr./Calle, N°, urbanización, referencia)" required>
              <input className={inputCls()} value={d.direccion_dom || ''} onChange={(e) => onChange({ ...d, direccion_dom: e.target.value })} />
            </Field>
          </div>
        </div>
      </div>

      <div>
        <h5 className="text-xs font-semibold uppercase text-gray-700 mb-2">Cargo y circunscripción</h5>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <div><span className="text-gray-500">Cargo:</span> <strong>{candidatoExt?.cargo ?? '—'}</strong></div>
          <div><span className="text-gray-500">N° posición:</span> <strong>{candidatoExt?.orden ?? '—'}</strong></div>
          <div><span className="text-gray-500">Región:</span> <strong>{candidatoExt?.region ?? '—'}</strong></div>
          <div><span className="text-gray-500">Provincia / Distrito:</span> <strong>{candidatoExt?.provincia ?? '—'} / {candidatoExt?.distrito ?? '—'}</strong></div>
          <div><span className="text-gray-500">Designado por CEN:</span> <strong>{candidatoExt?.es_designado ? 'Sí' : 'No'}</strong></div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Correo electrónico (casilla JNE)">
          <input className={inputCls()} type="email" value={d.email || ''} onChange={(e) => onChange({ ...d, email: e.target.value })} />
        </Field>
        <Field label="Teléfono celular">
          <input className={inputCls()} value={d.telefono || ''} onChange={(e) => onChange({ ...d, telefono: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

// ── S2 · Experiencia laboral ────────────────────────────────────────────────
function S2({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || { tiene: null, items: [] };
  const items = Array.isArray(d.items) ? d.items : [];
  const setItem = (i: number, patch: any) => onChange({ ...d, items: items.map((x: any, idx: number) => idx === i ? { ...x, ...patch } : x) });
  const add = () => items.length < 5 && onChange({ ...d, items: [...items, { sector: 'PRIVADO', centro: '', oficio: '', ruc: '', desde: '', hasta: '', pais: 'PE', dpto: '', prov: '', dist: '' }] });
  const del = (i: number) => onChange({ ...d, items: items.filter((_: any, idx: number) => idx !== i) });
  return (
    <div className="space-y-3">
      <Note>
        Últimos 10 años · máximo 5 registros · más reciente primero.
        Si país = Perú: completar Dpto/Prov/Dist. RUC es opcional.
      </Note>
      <YesNoToggle value={d.tiene} onChange={(v) => onChange({ ...d, tiene: v })} />

      {d.tiene === true && (
        <div className="space-y-2">
          {items.map((e: any, i: number) => (
            <div key={i} className="border rounded p-3 space-y-2">
              <div className="flex justify-between text-xs"><strong>Experiencia #{i + 1}</strong><button onClick={() => del(i)} className="text-red-600 hover:underline">eliminar</button></div>
              <div className="grid md:grid-cols-3 gap-2">
                <Field label="Sector"><select className={inputCls()} value={e.sector} onChange={(ev) => setItem(i, { sector: ev.target.value })}>
                  <option value="PUBLICO">Público</option><option value="PRIVADO">Privado</option><option value="INDEPENDIENTE">Independiente</option>
                </select></Field>
                <Field label="Centro de trabajo / empleador" required><input className={inputCls()} value={e.centro} onChange={(ev) => setItem(i, { centro: ev.target.value })} /></Field>
                <Field label="Oficio / ocupación" required><input className={inputCls()} value={e.oficio} onChange={(ev) => setItem(i, { oficio: ev.target.value })} /></Field>
                <Field label="RUC (opcional)"><input className={inputCls()} value={e.ruc} onChange={(ev) => setItem(i, { ruc: ev.target.value })} /></Field>
                <Field label="Desde (AAAA-MM)"><input className={inputCls()} value={e.desde} onChange={(ev) => setItem(i, { desde: ev.target.value })} placeholder="2020-03" /></Field>
                <Field label="Hasta (AAAA-MM)"><input className={inputCls()} value={e.hasta} onChange={(ev) => setItem(i, { hasta: ev.target.value })} placeholder="2024-12" /></Field>
                <Field label="País"><input className={inputCls()} value={e.pais} onChange={(ev) => setItem(i, { pais: ev.target.value })} placeholder="PE" /></Field>
                {(e.pais || '').toUpperCase() === 'PE' && <>
                  <Field label="Dpto"><input className={inputCls()} value={e.dpto} onChange={(ev) => setItem(i, { dpto: ev.target.value })} /></Field>
                  <Field label="Prov / Dist"><input className={inputCls()} value={`${e.prov || ''} / ${e.dist || ''}`} onChange={(ev) => { const [p, d] = ev.target.value.split('/').map(s => s.trim()); setItem(i, { prov: p, dist: d }); }} /></Field>
                </>}
              </div>
            </div>
          ))}
          {items.length < 5 && <button onClick={add} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar experiencia ({items.length}/5)</button>}
        </div>
      )}
      {d.tiene === false && <Note>Se registrará "No tengo información" en DECLARA+. El sistema bloqueará el campo.</Note>}
    </div>
  );
}

// ── S3 · Formación académica (6 subsecciones) ───────────────────────────────
const SUB3 = [
  { key: 'A', titulo: 'Educación básica regular', sub: ['primaria', 'secundaria'] },
  { key: 'B', titulo: 'Estudios técnicos', max: 5 },
  { key: 'C', titulo: 'Estudios no universitarios', max: 5 },
  { key: 'D', titulo: 'Estudios universitarios', max: 5 },
  { key: 'E', titulo: 'Estudios de posgrado', max: 5 },
  { key: 'F', titulo: 'Otros (diplomaturas, diplomados, etc.)', max: 5, noEgresado: true },
];

function S3({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const set = (k: string, v: any) => onChange({ ...d, [k]: v });
  const setItem = (k: string, i: number, patch: any) => {
    const items = (d[k]?.items || []).map((x: any, idx: number) => idx === i ? { ...x, ...patch } : x);
    set(k, { ...(d[k] || {}), items });
  };
  const add = (k: string, max = 5) => {
    const cur = d[k]?.items || [];
    if (cur.length >= max) return;
    set(k, { ...(d[k] || {}), items: [...cur, { institucion: '', titulo: '', estado: 'CONCLUIDO_EGRESADO', desde: '', hasta: '', info: '' }] });
  };
  const del = (k: string, i: number) => set(k, { ...(d[k] || {}), items: (d[k]?.items || []).filter((_: any, idx: number) => idx !== i) });

  return (
    <div className="space-y-5">
      <Note>Estructura DECLARA+: 6 subsecciones. Cada una requiere declarar Sí/No por separado.</Note>
      {SUB3.map((sub) => (
        <div key={sub.key} className="border rounded p-3 space-y-2">
          <h5 className="text-xs font-semibold text-arpp-primary">{sub.key}. {sub.titulo}</h5>
          <YesNoToggle value={d[sub.key]?.tiene} onChange={(v) => set(sub.key, { ...(d[sub.key] || {}), tiene: v })} />

          {sub.key === 'A' && d.A?.tiene === true && (
            <div className="grid md:grid-cols-2 gap-2 mt-2">
              <Field label="Primaria (¿concluida?)"><select className={inputCls()} value={d.A?.primaria || ''} onChange={(e) => set('A', { ...(d.A || {}), primaria: e.target.value })}>
                <option value="">— Seleccionar —</option><option value="CONCLUIDA">Concluida</option><option value="NO_CONCLUIDA">No concluida</option>
              </select></Field>
              <Field label="Secundaria (¿concluida?)"><select className={inputCls()} value={d.A?.secundaria || ''} onChange={(e) => set('A', { ...(d.A || {}), secundaria: e.target.value })}>
                <option value="">— Seleccionar —</option><option value="CONCLUIDA">Concluida</option><option value="NO_CONCLUIDA">No concluida</option>
              </select></Field>
            </div>
          )}

          {sub.key !== 'A' && d[sub.key]?.tiene === true && (
            <div className="space-y-2 mt-2">
              {(d[sub.key]?.items || []).map((e: any, i: number) => (
                <div key={i} className="border rounded p-2 grid md:grid-cols-3 gap-2 bg-gray-50">
                  <Field label="Institución / Universidad"><input className={inputCls()} value={e.institucion} onChange={(ev) => setItem(sub.key, i, { institucion: ev.target.value })} /></Field>
                  <Field label="Carrera / Grado / Título"><input className={inputCls()} value={e.titulo} onChange={(ev) => setItem(sub.key, i, { titulo: ev.target.value })} /></Field>
                  <Field label="Estado"><select className={inputCls()} value={e.estado} onChange={(ev) => setItem(sub.key, i, { estado: ev.target.value })}>
                    <option value="CONCLUIDO_EGRESADO">Concluido — egresado</option>
                    {!sub.noEgresado && <option value="CONCLUIDO_NO_EGRESADO">Concluido — no egresado</option>}
                    <option value="NO_CONCLUIDO">No concluido</option>
                  </select></Field>
                  <Field label="Desde"><input className={inputCls()} value={e.desde} onChange={(ev) => setItem(sub.key, i, { desde: ev.target.value })} placeholder="2010" /></Field>
                  <Field label="Hasta"><input className={inputCls()} value={e.hasta} onChange={(ev) => setItem(sub.key, i, { hasta: ev.target.value })} placeholder="2015" /></Field>
                  <Field label="Info complementaria"><input className={inputCls()} value={e.info} onChange={(ev) => setItem(sub.key, i, { info: ev.target.value })} /></Field>
                  <div className="md:col-span-3 text-right"><button onClick={() => del(sub.key, i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
                </div>
              ))}
              <button onClick={() => add(sub.key, sub.max)} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── S4 · Trayectoria partidaria ─────────────────────────────────────────────
function S4({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const subs = [
    { key: 'partidarios', titulo: 'A. Cargos partidarios (últimos 2)', desc: 'Presidente, secretario, coordinador, dirigente, etc.' },
    { key: 'eleccion_popular', titulo: 'B. Cargos de elección popular (últimos 2)', desc: 'Gobernador Regional, Alcalde, Regidor, Consejero, etc.' },
  ];
  const setItem = (k: string, i: number, patch: any) => {
    const items = (d[k]?.items || []).map((x: any, idx: number) => idx === i ? { ...x, ...patch } : x);
    onChange({ ...d, [k]: { ...(d[k] || {}), items } });
  };
  const add = (k: string) => {
    const cur = d[k]?.items || [];
    if (cur.length >= 2) return;
    onChange({ ...d, [k]: { ...(d[k] || {}), items: [...cur, { op: '', cargo: '', tipo: '', desde: '', hasta: '', info: '' }] } });
  };
  const del = (k: string, i: number) => onChange({ ...d, [k]: { ...(d[k] || {}), items: (d[k]?.items || []).filter((_: any, idx: number) => idx !== i) } });
  const setTiene = (k: string, v: boolean) => onChange({ ...d, [k]: { ...(d[k] || {}), tiene: v } });
  return (
    <div className="space-y-4">
      <Note>
        DECLARA+ solo registra los <strong>2 últimos cargos</strong> de cada tipo. Usar nomenclatura actual
        ('Gobernador Regional', no 'Presidente Regional'). OP del listado del ROP.
      </Note>
      {subs.map((s) => (
        <div key={s.key} className="border rounded p-3 space-y-2">
          <h5 className="text-xs font-semibold text-arpp-primary">{s.titulo}</h5>
          <p className="text-[11px] text-gray-600">{s.desc}</p>
          <YesNoToggle value={d[s.key]?.tiene} onChange={(v) => setTiene(s.key, v)} />
          {d[s.key]?.tiene === true && <div className="space-y-2 mt-2">
            {(d[s.key]?.items || []).map((e: any, i: number) => (
              <div key={i} className="border rounded p-2 grid md:grid-cols-3 gap-2 bg-gray-50">
                <Field label="OP (ROP)" required><input className={inputCls()} value={e.op} onChange={(ev) => setItem(s.key, i, { op: ev.target.value })} /></Field>
                <Field label="Cargo / función"><input className={inputCls()} value={e.cargo} onChange={(ev) => setItem(s.key, i, { cargo: ev.target.value })} /></Field>
                <Field label="Tipo"><input className={inputCls()} value={e.tipo} onChange={(ev) => setItem(s.key, i, { tipo: ev.target.value })} /></Field>
                <Field label="Desde"><input className={inputCls()} value={e.desde} onChange={(ev) => setItem(s.key, i, { desde: ev.target.value })} /></Field>
                <Field label="Hasta"><input className={inputCls()} value={e.hasta} onChange={(ev) => setItem(s.key, i, { hasta: ev.target.value })} /></Field>
                <Field label="Info"><input className={inputCls()} value={e.info} onChange={(ev) => setItem(s.key, i, { info: ev.target.value })} /></Field>
                <div className="md:col-span-3 text-right"><button onClick={() => del(s.key, i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
              </div>
            ))}
            {(d[s.key]?.items || []).length < 2 && <button onClick={() => add(s.key)} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar</button>}
          </div>}
        </div>
      ))}
    </div>
  );
}

// ── S5 · Sentencias penales ────────────────────────────────────────────────
function S5({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const setTiene = (v: boolean) => onChange({ ...d, declaro_tener_sentencias: v });
  const setItem = (i: number, patch: any) => onChange({ ...d, items: items.map((s: any, idx: number) => idx === i ? { ...s, ...patch } : s) });
  const add = () => onChange({ ...d, items: [...items, { expediente: '', organo: '', delito: '', fallo: '', modalidad: '', cumplimiento: '', info: '' }] });
  const del = (i: number) => onChange({ ...d, items: items.filter((_: any, idx: number) => idx !== i) });
  return (
    <div className="space-y-3">
      <Note intent="danger">
        <strong>CRÍTICO</strong>: omitir sentencias penales en DECLARA+ genera
        <strong> EXCLUSIÓN AUTOMÁTICA</strong> del candidato (Art. 15, Res. 0846-2025-JNE).
        Incluye delitos dolosos con sentencia firme y <em>reserva de fallo</em>.
        Debes marcar Sí o No explícitamente.
      </Note>
      <YesNoToggle value={d.declaro_tener_sentencias} onChange={setTiene} labelYes="Sí tengo sentencias" labelNo="No tengo sentencias" />
      {d.declaro_tener_sentencias === true && (
        <div className="space-y-2">
          {items.map((s: any, i: number) => (
            <div key={i} className="border rounded p-3 grid md:grid-cols-3 gap-2 bg-gray-50">
              <Field label="N° expediente" required><input className={inputCls()} value={s.expediente} onChange={(e) => setItem(i, { expediente: e.target.value })} /></Field>
              <Field label="Órgano Judicial"><input className={inputCls()} value={s.organo} onChange={(e) => setItem(i, { organo: e.target.value })} /></Field>
              <Field label="Delito"><input className={inputCls()} value={s.delito} onChange={(e) => setItem(i, { delito: e.target.value })} /></Field>
              <Field label="Fallo / pena"><input className={inputCls()} value={s.fallo} onChange={(e) => setItem(i, { fallo: e.target.value })} /></Field>
              <Field label="Modalidad"><input className={inputCls()} value={s.modalidad} onChange={(e) => setItem(i, { modalidad: e.target.value })} placeholder="suspendida / efectiva / reserva fallo" /></Field>
              <Field label="Cumplimiento"><input className={inputCls()} value={s.cumplimiento} onChange={(e) => setItem(i, { cumplimiento: e.target.value })} /></Field>
              <div className="md:col-span-3"><Field label="Info complementaria"><input className={inputCls()} value={s.info} onChange={(e) => setItem(i, { info: e.target.value })} /></Field></div>
              <div className="md:col-span-3 text-right"><button onClick={() => del(i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
            </div>
          ))}
          <button onClick={add} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar sentencia</button>
        </div>
      )}
    </div>
  );
}

// ── S6 · Sentencias no penales ─────────────────────────────────────────────
function S6({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const setItem = (i: number, patch: any) => onChange({ ...d, items: items.map((s: any, idx: number) => idx === i ? { ...s, ...patch } : s) });
  const add = () => onChange({ ...d, items: [...items, { materia: '', expediente: '', organo: '', fallo: '', info: '' }] });
  const del = (i: number) => onChange({ ...d, items: items.filter((_: any, idx: number) => idx !== i) });
  return (
    <div className="space-y-3">
      <Note>Civiles, alimentos, violencia familiar, obligaciones, etc.</Note>
      <YesNoToggle value={d.tiene} onChange={(v) => onChange({ ...d, tiene: v })} labelYes="Sí tengo sentencias" labelNo="No tengo sentencias" />
      {d.tiene === true && (
        <div className="space-y-2">
          {items.map((s: any, i: number) => (
            <div key={i} className="border rounded p-3 grid md:grid-cols-2 gap-2 bg-gray-50">
              <Field label="Materia"><input className={inputCls()} value={s.materia} onChange={(e) => setItem(i, { materia: e.target.value })} placeholder="alimentos / violencia / etc." /></Field>
              <Field label="N° expediente"><input className={inputCls()} value={s.expediente} onChange={(e) => setItem(i, { expediente: e.target.value })} /></Field>
              <Field label="Órgano"><input className={inputCls()} value={s.organo} onChange={(e) => setItem(i, { organo: e.target.value })} /></Field>
              <Field label="Fallo"><input className={inputCls()} value={s.fallo} onChange={(e) => setItem(i, { fallo: e.target.value })} /></Field>
              <div className="md:col-span-2"><Field label="Info complementaria"><input className={inputCls()} value={s.info} onChange={(e) => setItem(i, { info: e.target.value })} /></Field></div>
              <div className="md:col-span-2 text-right"><button onClick={() => del(i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
            </div>
          ))}
          <button onClick={add} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar</button>
        </div>
      )}
    </div>
  );
}

// ── S7 · Renuncias políticas ───────────────────────────────────────────────
function S7({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const setItem = (i: number, patch: any) => onChange({ ...d, items: items.map((r: any, idx: number) => idx === i ? { ...r, ...patch } : r) });
  const add = () => items.length < 2 && onChange({ ...d, items: [...items, { op: '', anio: '', info: '' }] });
  const del = (i: number) => onChange({ ...d, items: items.filter((_: any, idx: number) => idx !== i) });
  return (
    <div className="space-y-3">
      <Note>Máximo 2 renuncias. OP del listado del ROP.</Note>
      <YesNoToggle value={d.tiene} onChange={(v) => onChange({ ...d, tiene: v })} />
      {d.tiene === true && (
        <div className="space-y-2">
          {items.map((r: any, i: number) => (
            <div key={i} className="border rounded p-3 grid md:grid-cols-3 gap-2 bg-gray-50">
              <Field label="OP (nombre exacto ROP)" required><input className={inputCls()} value={r.op} onChange={(e) => setItem(i, { op: e.target.value })} /></Field>
              <Field label="Año de renuncia"><input className={inputCls()} value={r.anio} onChange={(e) => setItem(i, { anio: e.target.value })} /></Field>
              <Field label="Info (opcional)"><input className={inputCls()} value={r.info} onChange={(e) => setItem(i, { info: e.target.value })} /></Field>
              <div className="md:col-span-3 text-right"><button onClick={() => del(i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
            </div>
          ))}
          {items.length < 2 && <button onClick={add} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar ({items.length}/2)</button>}
        </div>
      )}
    </div>
  );
}

// ── S8 · Ingresos, bienes y rentas (2025) ──────────────────────────────────
function S8({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const ing = d.ingresos || {};
  const setIng = (patch: any) => onChange({ ...d, ingresos: { ...ing, ...patch } });

  function ListaBienes({ titulo, kind, fields }: { titulo: string; kind: string; fields: { name: string; label: string; type?: string; opts?: string[] }[] }) {
    const b = d[kind] || {};
    const items = Array.isArray(b.items) ? b.items : [];
    const setItem = (i: number, patch: any) => onChange({ ...d, [kind]: { ...b, items: items.map((x: any, idx: number) => idx === i ? { ...x, ...patch } : x) } });
    const add = () => onChange({ ...d, [kind]: { ...b, items: [...items, fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})] } });
    const del = (i: number) => onChange({ ...d, [kind]: { ...b, items: items.filter((_: any, idx: number) => idx !== i) } });
    return (
      <div className="border rounded p-3 space-y-2">
        <h5 className="text-xs font-semibold text-arpp-primary">{titulo}</h5>
        <YesNoToggle value={b.tiene} onChange={(v) => onChange({ ...d, [kind]: { ...b, tiene: v } })} />
        {b.tiene === true && (
          <div className="space-y-2 mt-2">
            {items.map((it: any, i: number) => (
              <div key={i} className="border rounded p-2 grid md:grid-cols-3 gap-2 bg-gray-50">
                {fields.map((f) => (
                  <Field key={f.name} label={f.label}>
                    {f.opts ? (
                      <select className={inputCls()} value={it[f.name] || ''} onChange={(e) => setItem(i, { [f.name]: e.target.value })}>
                        <option value="">—</option>
                        {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type || 'text'} className={inputCls()} value={it[f.name] || ''} onChange={(e) => setItem(i, { [f.name]: e.target.value })} />
                    )}
                  </Field>
                ))}
                <div className="md:col-span-3 text-right"><button onClick={() => del(i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
              </div>
            ))}
            <button onClick={add} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar</button>
          </div>
        )}
      </div>
    );
  }

  const num = (v: any) => v === '' || v === null || v === undefined ? '' : Number(v);
  const sumPub = (num(ing.quinta_publico) || 0) + (num(ing.cuarta_publico) || 0) + (num(ing.otros_publico) || 0);
  const sumPri = (num(ing.quinta_privado) || 0) + (num(ing.cuarta_privado) || 0) + (num(ing.otros_privado) || 0);

  return (
    <div className="space-y-4">
      <Note intent="warn">
        Si no percibes ingresos de un sector, ingresa <strong>0</strong>. DECLARA+ no acepta campos vacíos.
        Año fiscal 2025.
      </Note>

      <div className="border rounded p-3 space-y-2">
        <h5 className="text-xs font-semibold text-arpp-primary">A. Ingresos (S/)</h5>
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr><th className="p-1 text-left">Rubro</th><th className="p-1 text-right">Sector público</th><th className="p-1 text-right">Sector privado</th></tr>
          </thead>
          <tbody>
            <tr><td className="p-1">5ta categoría — planilla</td>
              <td><input type="number" className={inputCls()} value={ing.quinta_publico ?? ''} onChange={(e) => setIng({ quinta_publico: e.target.value === '' ? '' : Number(e.target.value) })} /></td>
              <td><input type="number" className={inputCls()} value={ing.quinta_privado ?? ''} onChange={(e) => setIng({ quinta_privado: e.target.value === '' ? '' : Number(e.target.value) })} /></td></tr>
            <tr><td className="p-1">4ta categoría — individual</td>
              <td><input type="number" className={inputCls()} value={ing.cuarta_publico ?? ''} onChange={(e) => setIng({ cuarta_publico: e.target.value === '' ? '' : Number(e.target.value) })} /></td>
              <td><input type="number" className={inputCls()} value={ing.cuarta_privado ?? ''} onChange={(e) => setIng({ cuarta_privado: e.target.value === '' ? '' : Number(e.target.value) })} /></td></tr>
            <tr><td className="p-1">Otros (predios, intereses, dietas, etc.)</td>
              <td><input type="number" className={inputCls()} value={ing.otros_publico ?? ''} onChange={(e) => setIng({ otros_publico: e.target.value === '' ? '' : Number(e.target.value) })} /></td>
              <td><input type="number" className={inputCls()} value={ing.otros_privado ?? ''} onChange={(e) => setIng({ otros_privado: e.target.value === '' ? '' : Number(e.target.value) })} /></td></tr>
            <tr className="bg-gray-50 font-semibold"><td className="p-1">TOTAL</td><td className="p-1 text-right">S/ {sumPub.toLocaleString('es-PE')}</td><td className="p-1 text-right">S/ {sumPri.toLocaleString('es-PE')}</td></tr>
          </tbody>
        </table>
      </div>

      <ListaBienes titulo="B. Bienes inmuebles" kind="inmuebles" fields={[
        { name: 'tipo', label: 'Tipo (casa/depto/terreno)' },
        { name: 'direccion', label: 'Dirección' },
        { name: 'sunarp', label: '¿SUNARP?', opts: ['Sí', 'No'] },
        { name: 'partida', label: 'N° partida (si SUNARP)' },
        { name: 'valor', label: 'Valor (S/)', type: 'number' },
        { name: 'autovaluo', label: 'Autovalúo (S/)', type: 'number' },
      ]} />

      <ListaBienes titulo="C. Bienes muebles (vehículos)" kind="vehiculos" fields={[
        { name: 'tipo', label: 'Tipo (auto/camioneta/moto)' },
        { name: 'placa', label: 'N° placa' },
        { name: 'caracteristicas', label: 'Marca / modelo / año' },
        { name: 'valor', label: 'Valor (S/)', type: 'number' },
      ]} />

      <ListaBienes titulo="D. Acciones y participaciones" kind="acciones" fields={[
        { name: 'empresa', label: 'Persona jurídica / empresa' },
        { name: 'tipo', label: 'Tipo', opts: ['Acción', 'Participación'] },
        { name: 'cantidad', label: 'N° acciones/partic.', type: 'number' },
        { name: 'valor_nominal', label: 'Valor nominal total (S/)', type: 'number' },
      ]} />
    </div>
  );
}

// ── S9 · Información adicional ─────────────────────────────────────────────
function S9({ data, onChange }: { data: any; onChange: (d: any) => void }) {
  const d = data || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const SECCION_OPTS = ['Datos personales', 'Experiencia', 'Formación', 'Trayectoria política', 'Sentencias penales', 'Sentencias no penales', 'Renuncias', 'Ingresos y bienes'];
  const setItem = (i: number, patch: any) => onChange({ ...d, items: items.map((it: any, idx: number) => idx === i ? { ...it, ...patch } : it) });
  const add = () => items.length < 4 && onChange({ ...d, items: [...items, { seccion: '', texto: '' }] });
  const del = (i: number) => onChange({ ...d, items: items.filter((_: any, idx: number) => idx !== i) });
  return (
    <div className="space-y-3">
      <Note>Opcional · permite aclarar cualquier rubro de la HDV. En DECLARA+ se elige la sección del desplegable.</Note>
      <YesNoToggle value={d.tiene} onChange={(v) => onChange({ ...d, tiene: v })} />
      {d.tiene === true && (
        <div className="space-y-2">
          {items.map((it: any, i: number) => (
            <div key={i} className="border rounded p-3 space-y-2 bg-gray-50">
              <Field label="Sección a complementar">
                <select className={inputCls()} value={it.seccion} onChange={(e) => setItem(i, { seccion: e.target.value })}>
                  <option value="">— Seleccionar —</option>
                  {SECCION_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Información adicional"><textarea rows={3} className={inputCls()} value={it.texto} onChange={(e) => setItem(i, { texto: e.target.value })} /></Field>
              <div className="text-right"><button onClick={() => del(i)} className="text-xs text-red-600 hover:underline">eliminar</button></div>
            </div>
          ))}
          {items.length < 4 && <button onClick={add} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Agregar ({items.length}/4)</button>}
        </div>
      )}
    </div>
  );
}

const FORMS: Record<number, React.ComponentType<any>> = { 1: S1, 2: S2, 3: S3, 4: S4, 5: S5, 6: S6, 7: S7, 8: S8, 9: S9 };

// ─── Wizard ────────────────────────────────────────────────────────────────
export function DjhvWizard({ candidatoId, canEdit }: { candidatoId: number; canEdit: boolean }) {
  const qc = useQueryClient();
  const [seccion, setSeccion] = useState(1);
  const [draft, setDraft] = useState<any>(null);

  const { data: djhv, isLoading } = useQuery<Djhv>({
    queryKey: ['djhv', candidatoId],
    queryFn: async () => (await api.get<Djhv>(`/candidatos/${candidatoId}/djhv`)).data,
  });

  const { data: candidato } = useQuery({
    queryKey: ['cand', String(candidatoId)],
    queryFn: async () => (await api.get(`/candidatos/${candidatoId}`)).data,
  });

  useEffect(() => {
    if (djhv) {
      const key = `S${seccion}`;
      setDraft(djhv.secciones?.[key] ?? null);
    }
  }, [djhv, seccion]);

  const saveMut = useMutation({
    mutationFn: async ({ marcar }: { marcar: boolean }) => {
      const { data } = await api.put<Djhv>(`/candidatos/${candidatoId}/djhv/seccion/${seccion}`, {
        data: draft ?? {},
        marcar_completa: marcar,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['djhv', candidatoId] }),
  });

  const confirmMut = useMutation({
    mutationFn: async () => (await api.post(`/candidatos/${candidatoId}/djhv/confirmar`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['djhv', candidatoId] }),
  });

  if (isLoading || !djhv) return <div className="text-sm text-gray-500 p-4">Cargando DJHV…</div>;

  const completas = new Set(djhv.secciones_completas || []);
  const SeccionForm = FORMS[seccion];
  const isReadonly = !canEdit || djhv.estado === 'confirmada_declara';

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded p-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-arpp-primary">Declaración Jurada Hoja de Vida (FUDJHV)</h3>
            <p className="text-[11px] text-gray-600">
              Res. N° 0846-2025-JNE · DECLARA+ · {djhv.secciones_completas.length}/9 secciones marcadas completas
            </p>
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded ${ESTADO_PILL[djhv.estado]}`}>
            {djhv.estado}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
          {SECCIONES.map((s) => (
            <button
              key={s.n}
              onClick={() => setSeccion(s.n)}
              className={`text-left border rounded p-2 transition ${
                completas.has(s.n)
                  ? 'bg-green-50 border-green-300'
                  : seccion === s.n
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="font-semibold text-gray-800 flex items-center gap-1">
                {completas.has(s.n) && <Check size={11} className="text-green-700" />}
                {s.titulo}
              </div>
              <div className="text-gray-600 mt-0.5">{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded">
        <nav className="grid grid-cols-9 border-b">
          {SECCIONES.map((s) => (
            <button
              key={s.n}
              onClick={() => setSeccion(s.n)}
              className={`p-2 text-xs flex flex-col items-center gap-0.5 border-r last:border-r-0 ${
                seccion === s.n
                  ? 'bg-arpp-primary text-white font-semibold'
                  : completas.has(s.n)
                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span>S{s.n}</span>
              {completas.has(s.n) && <Check size={12} />}
            </button>
          ))}
        </nav>

        <div className="p-4 space-y-3">
          <header>
            <h4 className="text-sm font-semibold">{SECCIONES[seccion - 1].titulo}</h4>
            <p className="text-[11px] text-gray-600">{SECCIONES[seccion - 1].desc}</p>
          </header>

          {seccion === 1 ? (
            <S1 data={draft} onChange={setDraft} candidatoExt={candidato} />
          ) : (
            <SeccionForm data={draft} onChange={setDraft} />
          )}

          {!isReadonly && (
            <div className="flex justify-between items-center pt-3 border-t">
              <button
                onClick={() => setSeccion((s) => Math.max(1, s - 1))}
                disabled={seccion === 1}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Anterior
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => saveMut.mutate({ marcar: false })}
                  disabled={saveMut.isPending}
                  className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded flex items-center gap-1"
                >
                  <Save size={12} /> Guardar borrador
                </button>
                <button
                  onClick={() => saveMut.mutate({ marcar: true })}
                  disabled={saveMut.isPending}
                  className="text-sm bg-arpp-primary text-white hover:bg-blue-800 px-3 py-1.5 rounded flex items-center gap-1"
                >
                  <Check size={12} /> Guardar y marcar completa
                </button>
              </div>
              <button
                onClick={() => setSeccion((s) => Math.min(9, s + 1))}
                disabled={seccion === 9}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 flex items-center gap-1"
              >
                Siguiente <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {canEdit && djhv.estado === 'completa' && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold">¿Confirmar DJHV?</h4>
            <p className="text-[11px] text-gray-700">
              Marca como confirmada en DECLARA+. Solo PLT/PLA/ADM puede confirmar
              (después de verificar FUDJHV en DECLARA+).
            </p>
          </div>
          <button
            onClick={() => confirmMut.mutate()}
            disabled={confirmMut.isPending}
            className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
          >
            Confirmar DJHV
          </button>
        </div>
      )}
    </div>
  );
}
